import { useRef } from 'react'
import type { PointerEvent as ReactPointerEvent, RefObject } from 'react'
import type { PannableScrollViewport } from './use-pannable-scroll'

export interface ScrollMinimapSegment {
    key: string | number
    label: string
}

interface ScrollMinimapProps {
    /** Container rolável observado (o mesmo `ref` de `usePannableScroll`). */
    targetRef: RefObject<HTMLElement | null>
    viewport: PannableScrollViewport
    /** Faixas de fundo — nas tabelas, uma por coluna, para dar orientação. */
    segments?: ScrollMinimapSegment[]
    className?: string
}

/**
 * Barra de navegação horizontal arrastável, no padrão do quadro de funil.
 *
 * Clicar fora da janela centraliza nela; clicar dentro e arrastar preserva o ponto
 * de agarre (`grabOffset`) — sem isso a janela "pula" para o cursor no primeiro
 * pixel de movimento, que é a diferença entre parecer um scrollbar e parecer um bug.
 */
export default function ScrollMinimap({
    targetRef,
    viewport,
    segments = [],
    className = '',
}: ScrollMinimapProps) {
    const isDragging = useRef(false)
    const grabOffset = useRef(0)

    /** Converte uma razão (0-1) da barra em `scrollLeft`, respeitando os limites. */
    const scrollToRatio = (target: HTMLElement, ratio: number) => {
        target.scrollLeft = Math.max(
            0,
            Math.min(ratio * target.scrollWidth, target.scrollWidth - target.clientWidth)
        )
    }

    const handlePointerDown = (e: ReactPointerEvent) => {
        const target = targetRef.current
        const minimap = e.currentTarget as HTMLElement
        if (!target) return

        const rect = minimap.getBoundingClientRect()
        const clickRatio = (e.clientX - rect.left) / rect.width
        const leftRatio = viewport.left / 100
        const widthRatio = viewport.width / 100

        const insideViewport =
            clickRatio >= leftRatio && clickRatio <= leftRatio + widthRatio

        if (insideViewport) {
            grabOffset.current = clickRatio - leftRatio
        } else {
            // Clique fora: a janela salta centrada no cursor.
            grabOffset.current = widthRatio / 2
            scrollToRatio(target, clickRatio - grabOffset.current)
        }

        isDragging.current = true
        minimap.setPointerCapture(e.pointerId)
    }

    const handlePointerMove = (e: ReactPointerEvent) => {
        if (!isDragging.current) return
        const target = targetRef.current
        const minimap = e.currentTarget as HTMLElement
        if (!target) return

        const rect = minimap.getBoundingClientRect()
        const ratio = (e.clientX - rect.left) / rect.width
        scrollToRatio(target, ratio - grabOffset.current)
    }

    const handlePointerUp = (e: ReactPointerEvent) => {
        if (!isDragging.current) return
        isDragging.current = false
        ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
    }

    return (
        <div
            className={`relative h-7 rounded-md overflow-hidden select-none touch-none bg-base-200 cursor-crosshair ${className}`}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            role="scrollbar"
            aria-label="Navegação horizontal da tabela"
            aria-orientation="horizontal"
            aria-valuenow={Math.round(viewport.left)}
        >
            {segments.length > 0 && (
                <div className="absolute inset-0 flex items-center gap-0.5 px-1 pointer-events-none">
                    {segments.map((segment) => (
                        <div
                            key={segment.key}
                            className="h-4 rounded-sm flex-1 min-w-0 flex items-center justify-center bg-base-300"
                        >
                            <span className="text-[7px] font-medium text-base-content/40 truncate px-0.5">
                                {segment.label}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {/* Janela visível */}
            <div
                className="absolute top-0 bottom-0 rounded-md border-2 border-secondary bg-secondary/10 pointer-events-none transition-[left] duration-75"
                style={{
                    left: `${viewport.left}%`,
                    width: `${viewport.width}%`,
                }}
            />
        </div>
    )
}
